import {
  useWSModalCustom,
  WSForm,
  WSInputDate,
  WSInputText,
  WSList,
  WSModal,
  WSModalCustomProps,
  WSSelect,
  toWSDateString
} from "@wingspanhq/fe-component-library";
import { ApiKey, ApiKeyWithSecret } from "@wingspanhq/wingspan-api-sdk";
import { addDays } from "date-fns";
import React from "react";
import * as Yup from "yup";
import { ErrorAlert } from "@components/ErrorAlert";
import { queriesApiKeys, queriesAuthorizations } from "@query";
import {
  EXPIRATION_IN_DAYS,
  getExpirationSelectOptions
} from "../utils/developerUtils";

type ApiKeyFormData = {
  name: string;
  scopes: string[];
  expiration: string;
  customExpiryDate?: Date;
};

type Props = {
  apiKey?: ApiKey;
} & WSModalCustomProps<ApiKeyWithSecret | null>;

const resolveExpiresAt = (data: ApiKeyFormData): Date | undefined => {
  if (EXPIRATION_IN_DAYS.includes(data.expiration)) {
    return addDays(new Date(), Number(data.expiration));
  }
  if (data.expiration === "CUSTOM") {
    return data.customExpiryDate;
  }
  return undefined;
};

const getDefaultExpiration = (apiKey?: ApiKey): string => {
  if (!apiKey) {
    return "30";
  }
  return apiKey.expiresAt ? "CUSTOM" : "NO_EXPIRATION";
};

const ModalApiKeyForm: React.FC<Props> = props => {
  const isEdit = !!props.apiKey;

  const queryScopes = queriesAuthorizations.listScopes.useQuery();
  const [createApiKey, createMeta] = queriesApiKeys.createApiKey.useMutation();
  const [updateApiKey, updateMeta] = queriesApiKeys.updateApiKey.useMutation();
  const meta = isEdit ? updateMeta : createMeta;

  const scopeOptions = (queryScopes.data ?? [])
    .filter(scope => !!scope.name)
    .map(scope => ({
      value: scope.name as string,
      label: scope.name as string
    }));

  const handleSubmit = async (data: ApiKeyFormData) => {
    const expiresAt = resolveExpiresAt(data);

    if (props.apiKey) {
      await updateApiKey(
        {
          apiKeyId: props.apiKey.id,
          apiKeyUpdate: {
            name: data.name,
            ...(data.scopes.length
              ? { scopes: data.scopes }
              : { clearScopes: true }),
            ...(expiresAt ? { expiresAt } : {})
          }
        },
        { onSuccess: () => props.onClose(null) }
      );
    } else {
      await createApiKey(
        {
          apiKeyCreate: {
            name: data.name,
            scopes: data.scopes,
            ...(expiresAt ? { expiresAt } : {})
          }
        },
        { onSuccess: created => props.onClose(created) }
      );
    }
  };

  return (
    <WSForm<ApiKeyFormData>
      defaultValues={{
        name: props.apiKey?.name ?? "",
        scopes: props.apiKey?.scopes ?? [],
        expiration: getDefaultExpiration(props.apiKey),
        customExpiryDate: props.apiKey?.expiresAt ?? undefined
      }}
      validationSchema={Yup.object().shape({
        name: Yup.string().required("Name is required."),
        expiration: Yup.string().required("Expiration is required."),
        customExpiryDate: Yup.date().when("expiration", {
          is: "CUSTOM",
          then: Yup.date().required("Custom expiry date is required.")
        })
      })}
      onSubmit={handleSubmit}
    >
      <WSModal
        size="S"
        title={isEdit ? "Edit API key" : "New API key"}
        onClose={() => props.onClose(null)}
        actions={{
          buttons: [
            {
              label: "Cancel",
              kind: "Secondary",
              type: "button",
              onClick: () => props.onClose(null)
            },
            {
              label: isEdit ? "Save" : "Generate",
              type: "submit",
              loading: meta.isLoading
            }
          ]
        }}
      >
        <WSList gap="L">
          <WSForm.Field
            name="name"
            label="What's this key for?"
            component={WSInputText}
            componentProps={{ placeholder: "e.g. Production integration" }}
          />

          <WSForm.Field
            name="scopes"
            label="Scopes (optional)"
            component={WSSelect}
            componentProps={{
              mode: "multiple",
              placeholder: "Select scopes",
              internalSearch: true,
              isListLoading: queryScopes.isLoading,
              options: scopeOptions
            }}
          />

          <WSForm.Value name="expiration">
            {(expiration: string) => (
              <WSForm.Field
                name="expiration"
                label="Expiration"
                component={WSSelect}
                componentProps={{
                  hideClearAction: true,
                  options: getExpirationSelectOptions()
                }}
                description={
                  EXPIRATION_IN_DAYS.includes(expiration)
                    ? `The key will expire on ${toWSDateString(
                        addDays(new Date(), Number(expiration)),
                        "monthDayYear"
                      )}`
                    : undefined
                }
              />
            )}
          </WSForm.Value>

          <WSForm.Value name="expiration">
            {(expiration: string) =>
              expiration === "CUSTOM" ? (
                <WSForm.Field
                  name="customExpiryDate"
                  label="Custom expiry date"
                  component={WSInputDate}
                  componentProps={{ minDate: new Date() }}
                />
              ) : null
            }
          </WSForm.Value>

          <ErrorAlert context="ApiKeySave" error={meta.error} />
        </WSList>
      </WSModal>
    </WSForm>
  );
};

export const useModalApiKeyForm = () => useWSModalCustom(ModalApiKeyForm);
